type QualifiedTrafficConfig = {
  enabled?: boolean;
  patterns?: string[];
};

type QualifiedTrafficWindow = typeof window & {
  qualifiedTraffic?: QualifiedTrafficConfig;
};

const patternCache = new Map<string, RegExp>();

const toRegExp = (rawPattern: string): RegExp | null => {
  const cached = patternCache.get(rawPattern);
  if (cached) {
    return cached;
  }

  if (!rawPattern) {
    return null;
  }

  const trimmed = rawPattern.trim();

  if (!trimmed) {
    return null;
  }

  let source = trimmed;
  let flags = "";

  if (trimmed.startsWith("/") && trimmed.lastIndexOf("/") > 0) {
    const lastSlash = trimmed.lastIndexOf("/");
    source = trimmed.slice(1, lastSlash);
    flags = trimmed.slice(lastSlash + 1);
  }

  try {
    const expr = new RegExp(source, flags);
    patternCache.set(rawPattern, expr);
    return expr;
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn(
        "identifyQualifiedTraffic: invalid regex pattern",
        rawPattern,
        error,
      );
    }
    return null;
  }
};

const getQualifiedTrafficPatterns = (): RegExp[] => {
  if (typeof window === "undefined") {
    return [];
  }

  const { qualifiedTraffic } = (window as QualifiedTrafficWindow) ?? {};
  const { enabled, patterns } = qualifiedTraffic ?? {};

  if (!enabled || !Array.isArray(patterns) || patterns.length === 0) {
    return [];
  }

  return patterns
    .map(toRegExp)
    .filter((pattern): pattern is RegExp => Boolean(pattern));
};

export const identifyQualifiedTraffic = (
  url: string | undefined = typeof window !== "undefined"
    ? window.location.href
    : undefined,
): { qualified_traffic_from_web: boolean } => {
  if (!url) {
    return { qualified_traffic_from_web: false };
  }

  const qualifiedPatterns = getQualifiedTrafficPatterns();

  if (qualifiedPatterns.length === 0) {
    return { qualified_traffic_from_web: false };
  }

  const isQualified = qualifiedPatterns.some((pattern) => pattern.test(url));

  return { qualified_traffic_from_web: isQualified };
};

export default identifyQualifiedTraffic;
